import { Component } from '@verndale/core';

// Static manager for navigation accordions
class NavigationAccordionManager {
  private static instances: Set<Accordion> = new Set();

  static register(accordion: Accordion) {
    this.instances.add(accordion);
  }

  static closeOthers(current: Accordion) {
    this.instances.forEach(accordion => {
      if (accordion !== current && accordion.isNavigation) {
        accordion.closeAllItems();
      }
    });
  }
}

class Accordion extends Component {
  shouldPreventAnimation: boolean;
  isNavigation: boolean;

  constructor(el: HTMLElement) {
    super(el);
    this.shouldPreventAnimation = false;
    // Check if this accordion is part of the navigation menu
    this.isNavigation = el.closest('.navigation-menu__level-3-menu-items') !== null;

    if (this.isNavigation) {
      NavigationAccordionManager.register(this);
    }
  }

  setupDefaults() {
    this.dom = {
      triggers: this.el.querySelectorAll('.accordion__item-trigger'),
      panels: this.el.querySelectorAll('.accordion__item-panel')
    };

    this.initAccordion();
    this.shouldPreventAnimation = false;
  }

  addListeners() {
    (this.dom.triggers as NodeList).forEach(trigger =>
      trigger.addEventListener('click', this.handleClick.bind(this))
    );
  }

  initAccordion() {
    (this.dom.triggers as NodeListOf<HTMLButtonElement>).forEach(trigger => {
      if (trigger.getAttribute('aria-expanded') === 'true') {
        this.openItem(trigger);
      }
    });
  }

  handleClick(e: Event) {
    const isCollapsable = this.el.dataset.isCollapsable === 'true';
    const trigger = e.currentTarget as HTMLButtonElement;

    if (isCollapsable) {
      if (trigger.getAttribute('aria-expanded') === 'true') {
        this.closeItem(trigger);
      } else {
        if (this.isNavigation) {
          NavigationAccordionManager.closeOthers(this);
        }
        // Close other items in the same accordion
        (this.dom.triggers as NodeListOf<HTMLElement>).forEach(item => {
          if (item !== trigger && item.getAttribute('aria-expanded') === 'true') {
            this.closeItem(item as HTMLButtonElement);
          }
        });
        this.openItem(trigger);
      }
    } else {
      if (trigger.getAttribute('aria-expanded') === 'true') {
        this.closeItem(trigger);
      } else {
        this.openItem(trigger);
      }
    }
  }

  openItem(trigger: HTMLButtonElement) {
    trigger.setAttribute('aria-expanded', 'true');
    const panel = document.getElementById(trigger.getAttribute('aria-controls') as string);

    if (panel) {
      panel.style.display = 'block';
      const pannelContent = panel.querySelector<HTMLElement>('.accordion__item-content');

      // Force reflow
      panel.offsetHeight;

      panel.style.height = `${pannelContent?.offsetHeight}px`;

      setTimeout(() => {
        panel.style.height = 'auto';
      }, 250);
    }
  }

  closeItem(trigger: HTMLButtonElement) {
    trigger.setAttribute('aria-expanded', 'false');
    const panel = document.getElementById(trigger.getAttribute('aria-controls') as string);
    if (panel) {
      const pannelContent = panel.querySelector<HTMLElement>('.accordion__item-content');
      panel.style.height = `${pannelContent?.offsetHeight}px`;

      // Force reflow
      panel.offsetHeight;

      panel.style.height = '0px';

      setTimeout(() => {
        panel.style.display = 'none';
      }, 250);
    }
  }

  closeAllItems() {
    (this.dom.triggers as NodeListOf<HTMLElement>).forEach(trigger => {
      this.closeItem(trigger as HTMLButtonElement);
    });
  }

  openAllItems() {
    (this.dom.triggers as NodeListOf<HTMLElement>).forEach(trigger => {
      this.openItem(trigger as HTMLButtonElement);
    });
  }
}

export default Accordion;
